import { useCallback } from 'react';
import {
  postPartnerImpersonate,
  postPartnerImpersonationEnd,
} from '@/shared/api/partner';
import {
  clearImpersonation,
  getImpersonationClientName,
  getImpersonationId,
  getImpersonationToken,
  setImpersonation,
} from './storage';

export interface UseImpersonationResult {
  isImpersonating: boolean;
  clientName: string | null;
  enter: (input: { clientAccountId: string; clientName: string }) => Promise<void>;
  exit: () => Promise<void>;
}

/**
 * Manages the partner→client impersonation lifecycle.
 *
 * enter(): POST /api/v1/auth/partner/clients/:id/impersonate, store the token
 * in sessionStorage, then hard-navigate to the client landing page so all
 * axios instances pick up the Bearer header injected by the interceptor and
 * Centrifugo re-subscribes to the client account channel. We navigate to '/'
 * (not reload) because the partner is on /affiliate when they click "Войти" —
 * staying there would render partner UI under client identity (every
 * /api/v1/partner/* call returns 403). Hard-navigate to '/' lands the user
 * on the client's natural landing page.
 *
 * exit(): best-effort POST /end (audit precision), clear sessionStorage,
 * hard-navigate back to /affiliate (partner's landing).
 */
export function useImpersonation(): UseImpersonationResult {
  const enter = useCallback(async (input: { clientAccountId: string; clientName: string }) => {
    const result = await postPartnerImpersonate(input.clientAccountId);
    setImpersonation({
      token:           result.token,
      impersonationId: result.impersonationId,
      clientName:      input.clientName,
    });
    // Hard-navigate (not reload) so we leave any partner-only route like
    // /affiliate. window.location.assign triggers a full page load — same
    // benefits as reload (axios instances re-init, Centrifugo re-subscribes).
    window.location.assign('/');
  }, []);

  const exit = useCallback(async () => {
    const id = getImpersonationId();
    if (id) {
      // Best-effort: ignore errors so a stale token never traps the partner
      // in the impersonated cabinet. Clearing sessionStorage and navigating
      // always succeeds.
      try {
        await postPartnerImpersonationEnd(id);
      } catch {
        /* swallow */
      }
    }
    clearImpersonation();
    // Land back on the partner cabinet — that's where they came from.
    window.location.assign('/affiliate');
  }, []);

  return {
    isImpersonating: getImpersonationToken() !== null,
    clientName:      getImpersonationClientName(),
    enter,
    exit,
  };
}
